import { useMemo } from "react";

import {
    UserVaultPositionExpanded,
    UserVaultPnl,
    StakeStatus,
    useUserVaultPnlFromDepositSummary,
    VaultDepositSummary,
    isRecentVaultDepositOrWithdrawal,
    RewardMint,
    UserExternalRewardsInfoExpanded,
    useOraclePrices
} from "@bridgesplit/abf-react";
import { Column, MediaQuery, OverlappingImages, Row, Tooltip, Text, StatProps, Image } from "@bridgesplit/ui";
import { useMediaQuery } from "@mui/material";
import { PositionCardStats, VaultProfitLoss } from "app/components/common";
import { COPY } from "app/constants";
import { formatDate, formatTokenAmount, formatUsd } from "@bridgesplit/utils";

import { VaultAmountDisplay } from "./VaultAmountDisplay";
import { VaultPositionApy } from "./VaultPositionCard";
import { VaultPendingPnl } from "./VaultPendingPnl";

const MAX_EXTERNAL_REWARD_IMAGES = 5;

export function VaultPositionStats({
    vaultPosition,
    query,
    status,
    depositSummary,
    externalRewards
}: {
    vaultPosition: UserVaultPositionExpanded | undefined;
    query: MediaQuery;
    status?: StakeStatus;
    depositSummary: VaultDepositSummary | undefined;
    externalRewards: Record<RewardMint, UserExternalRewardsInfoExpanded> | undefined;
}) {
    const isMobile = useMediaQuery(query.below);
    const userVaultPnl = useUserVaultPnlFromDepositSummary(depositSummary, vaultPosition?.vaultExpanded, vaultPosition);
    const stats = getVaultPositionStats(vaultPosition, isMobile, userVaultPnl, externalRewards, status);

    return <PositionCardStats loading={!vaultPosition || !userVaultPnl} stats={stats} forceColumn={!isMobile} />;
}
function VaultPositionValue({ vaultPosition }: { vaultPosition: UserVaultPositionExpanded | undefined }) {
    return <VaultAmountDisplay vaultPosition={vaultPosition} variant="total" />;
}

function getVaultPositionStats(
    vaultPosition: UserVaultPositionExpanded | undefined,
    isMobile: boolean,
    userVaultPnl: UserVaultPnl | undefined,
    externalRewards: Record<RewardMint, UserExternalRewardsInfoExpanded> | undefined,
    status?: StakeStatus
) {
    const showPnl = status === StakeStatus.Active;
    const isPnlPending = isRecentVaultDepositOrWithdrawal(userVaultPnl?.mostRecentDepositOrWithdrawalTimestamp);

    const stats: StatProps[] = [
        {
            value: <VaultPositionApy vaultPosition={vaultPosition} />,
            caption: "APY",
            hide: !isMobile
        },
        {
            value: <VaultPositionValue vaultPosition={vaultPosition} />,
            caption: COPY.USER_POSITION_TERM
        },
        {
            value: (
                <VaultProfitLoss
                    variant="body1"
                    vaultPosition={vaultPosition}
                    userVaultPnl={userVaultPnl}
                    isPending={isPnlPending}
                />
            ),
            caption: COPY.INTEREST_EARNED_LABEL,
            hide: !showPnl || isPnlPending
        },
        {
            value: <VaultPendingPnl />,
            caption: COPY.INTEREST_EARNED_LABEL,
            hide: !isPnlPending
        },
        {
            value: formatDate(vaultPosition?.startTime),
            caption: "Last interacted"
        },
        {
            value: <VaultExternalRewards externalRewards={externalRewards} />,
            caption: "External rewards",
            hide: !externalRewards
        }
    ].filter((stat) => !stat.hide);

    return stats;
}

function VaultExternalRewards({
    externalRewards
}: {
    externalRewards: Record<RewardMint, UserExternalRewardsInfoExpanded> | undefined;
}) {
    const rewards = useMemo(() => {
        return externalRewards ? Object.values(externalRewards) : [];
    }, [externalRewards]);

    const images = useMemo(() => {
        return rewards.map((r) => ({ src: r.rewardMetadata.imageUrl, variant: "circle" as const }));
    }, [rewards]);

    if (rewards.length === 0) {
        return null;
    }

    return (
        <Tooltip
            placement="bottom-start"
            minTooltipWidth="200px"
            reverseColors
            title={<VaultExternalRewardsTooltip rewards={rewards} />}
        >
            <OverlappingImages images={images} size="20px" maxLength={MAX_EXTERNAL_REWARD_IMAGES} />
        </Tooltip>
    );
}

function VaultExternalRewardsTooltip({ rewards }: { rewards: UserExternalRewardsInfoExpanded[] }) {
    const mints = useMemo(() => rewards.map((r) => r.rewardMint), [rewards]);
    const { getOracle } = useOraclePrices(mints);

    return (
        <Column padding={0.5} spacing={0.5}>
            {rewards.map((reward) => {
                const usdPrice = getOracle(reward.rewardMint)?.usdPrice ?? 0;
                const usdValue = (reward.pendingRewards ?? 0) * usdPrice;

                return (
                    <Row key={reward.rewardMint} sx={{ alignItems: "center", gap: 1 }} spaceBetween>
                        <Row spacing={0.5}>
                            <Image src={reward.rewardMetadata.imageUrl} size="16px" variant="circle" />
                            <Text variant="body2">{reward.rewardMetadata.symbol}</Text>
                        </Row>
                        <Row spacing={0.5}>
                            <Text variant="body2">
                                {formatTokenAmount(reward.pendingRewards, {
                                    symbol: reward.rewardMetadata.symbol,
                                    hideSymbol: true
                                })}
                            </Text>
                            <Text variant="body2" color="disabled">
                                {formatUsd(usdValue)}
                            </Text>
                        </Row>
                    </Row>
                );
            })}
        </Column>
    );
}
